import { useState, useEffect, useRef } from "react";
import {
  FaQrcode,
  FaChartLine,
  FaUsers,
  FaMobileAlt,
  FaChevronLeft,
  FaChevronRight,
  FaDesktop,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import RollingGallery from "./RollingGallery";

// --- Particles Component (self-contained) ---
import { Renderer, Camera, Geometry, Program, Mesh } from "ogl";

const defaultColors = ["#ffffff", "#ffffff", "#ffffff"];

const hexToRgb = (hex) => {
  hex = hex.replace(/^#/, "");
  if (hex.length === 3) {
    hex = hex
      .split("")
      .map((c) => c + c)
      .join("");
  }
  const int = parseInt(hex, 16);
  const r = ((int >> 16) & 255) / 255;
  const g = ((int >> 8) & 255) / 255;
  const b = (int & 255) / 255;
  return [r, g, b];
};

const vertex = /* glsl */ `
  attribute vec3 position;
  attribute vec4 random;
  attribute vec3 color;
  
  uniform mat4 modelMatrix;
  uniform mat4 viewMatrix;
  uniform mat4 projectionMatrix;
  uniform float uTime;
  uniform float uSpread;
  uniform float uBaseSize;
  uniform float uSizeRandomness;
  
  varying vec4 vRandom;
  varying vec3 vColor;
  
  void main() {
    vRandom = random;
    vColor = color;
    
    vec3 pos = position * uSpread;
    pos.z *= 10.0;
    
    vec4 mPos = modelMatrix * vec4(pos, 1.0);
    float t = uTime;
    mPos.x += sin(t * random.z + 6.28 * random.w) * mix(0.1, 1.5, random.x);
    mPos.y += sin(t * random.y + 6.28 * random.x) * mix(0.1, 1.5, random.w);
    mPos.z += sin(t * random.w + 6.28 * random.y) * mix(0.1, 1.5, random.z);
    
    vec4 mvPos = viewMatrix * mPos;
    gl_PointSize = (uBaseSize * (1.0 + uSizeRandomness * (random.x - 0.5))) / length(mvPos.xyz);
    gl_Position = projectionMatrix * mvPos;
  }
`;

const fragment = /* glsl */ `
  precision highp float;
  
  uniform float uTime;
  uniform float uAlphaParticles;
  varying vec4 vRandom;
  varying vec3 vColor;
  
  void main() {
    vec2 uv = gl_PointCoord.xy;
    float d = length(uv - vec2(0.5));
    
    if(uAlphaParticles < 0.5) {
      if(d > 0.5) {
        discard;
      }
      gl_FragColor = vec4(vColor + 0.2 * sin(uv.yxx + uTime + vRandom.y * 6.28), 1.0);
    } else {
      float circle = smoothstep(0.5, 0.4, d) * 0.8;
      gl_FragColor = vec4(vColor + 0.2 * sin(uv.yxx + uTime + vRandom.y * 6.28), circle);
    }
  }
`;

const Particles = ({
  particleCount = 200,
  particleSpread = 10,
  speed = 0.1,
  particleColors,
  moveParticlesOnHover = false,
  particleHoverFactor = 1,
  alphaParticles = false,
  particleBaseSize = 100,
  sizeRandomness = 1,
  cameraDistance = 20,
  disableRotation = false,
  className,
}) => {
  const containerRef = useRef(null);
  const mouseRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const renderer = new Renderer({ depth: false, alpha: true });
    const gl = renderer.gl;
    container.appendChild(gl.canvas);
    gl.clearColor(0, 0, 0, 0);

    const camera = new Camera(gl, { fov: 15 });
    camera.position.set(0, 0, cameraDistance);

    const resize = () => {
      const width = container.clientWidth;
      const height = container.clientHeight;
      renderer.setSize(width, height);
      camera.perspective({ aspect: gl.canvas.width / gl.canvas.height });
    };
    window.addEventListener("resize", resize, false);
    resize();

    const handleMouseMove = (e) => {
      const rect = container.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      const y = -(((e.clientY - rect.top) / rect.height) * 2 - 1);
      mouseRef.current = { x, y };
    };

    if (moveParticlesOnHover) {
      container.addEventListener("mousemove", handleMouseMove);
    }

    const count = particleCount;
    const positions = new Float32Array(count * 3);
    const randoms = new Float32Array(count * 4);
    const colors = new Float32Array(count * 3);
    const palette =
      particleColors && particleColors.length > 0
        ? particleColors
        : defaultColors;

    for (let i = 0; i < count; i++) {
      let x, y, z, len;
      do {
        x = Math.random() * 2 - 1;
        y = Math.random() * 2 - 1;
        z = Math.random() * 2 - 1;
        len = x * x + y * y + z * z;
      } while (len > 1 || len === 0);
      const r = Math.cbrt(Math.random());
      positions.set([x * r, y * r, z * r], i * 3);
      randoms.set(
        [Math.random(), Math.random(), Math.random(), Math.random()],
        i * 4
      );
      const col = hexToRgb(palette[Math.floor(Math.random() * palette.length)]);
      colors.set(col, i * 3);
    }

    const geometry = new Geometry(gl, {
      position: { size: 3, data: positions },
      random: { size: 4, data: randoms },
      color: { size: 3, data: colors },
    });

    const program = new Program(gl, {
      vertex,
      fragment,
      uniforms: {
        uTime: { value: 0 },
        uSpread: { value: particleSpread },
        uBaseSize: { value: particleBaseSize },
        uSizeRandomness: { value: sizeRandomness },
        uAlphaParticles: { value: alphaParticles ? 1 : 0 },
      },
      transparent: true,
      depthTest: false,
    });

    const particles = new Mesh(gl, { mode: gl.POINTS, geometry, program });

    let animationFrameId;
    let lastTime = performance.now();
    let elapsed = 0;

    const update = (t) => {
      animationFrameId = requestAnimationFrame(update);
      const delta = t - lastTime;
      lastTime = t;
      elapsed += delta * speed;

      program.uniforms.uTime.value = elapsed * 0.001;

      if (moveParticlesOnHover) {
        particles.position.x = -mouseRef.current.x * particleHoverFactor;
        particles.position.y = -mouseRef.current.y * particleHoverFactor;
      } else {
        particles.position.x = 0;
        particles.position.y = 0;
      }

      if (!disableRotation) {
        particles.rotation.x = Math.sin(elapsed * 0.0002) * 0.1;
        particles.rotation.y = Math.cos(elapsed * 0.0005) * 0.15;
        particles.rotation.z += 0.01 * speed;
      }

      renderer.render({ scene: particles, camera });
    };

    animationFrameId = requestAnimationFrame(update);

    return () => {
      window.removeEventListener("resize", resize);
      if (moveParticlesOnHover) {
        container.removeEventListener("mousemove", handleMouseMove);
      }
      cancelAnimationFrame(animationFrameId);
      if (container.contains(gl.canvas)) {
        container.removeChild(gl.canvas);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    particleCount,
    particleSpread,
    speed,
    moveParticlesOnHover,
    particleHoverFactor,
    alphaParticles,
    particleBaseSize,
    sizeRandomness,
    cameraDistance,
    disableRotation,
  ]);

  return (
    <div
      ref={containerRef}
      className={`absolute inset-0 w-full h-full pointer-events-none z-0 ${
        className || ""
      }`}
      style={{}}
    />
  );
};
// --- End Particles Component ---

export default function HeroSlider({ shrink }) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const navigate = useNavigate();

  const slides = [
    {
      title: "Transform Your Restaurant with Digital Menus",
      subtitle:
        "QR codes, contactless ordering, real-time analytics - all in one platform",
      description:
        "Revolutionize your restaurant operations with our comprehensive QR code ordering system. Manage menus, track orders, and boost customer satisfaction with cutting-edge technology.",
      cta: "Start Free Trial",
      icon: (
        <FaQrcode className="text-4xl sm:text-5xl md:text-6xl text-orange-500" />
      ),
      bgGradient: "from-orange-500 via-red-500 to-amber-500",
      features: [
        "QR Code Menus",
        "Contactless Ordering",
        "Real-time Analytics",
      ],
    },
    {
      title: "One QR Code, Infinite Possibilities",
      subtitle:
        "Generate unique QR codes for each table and streamline your operations",
      description:
        "Each table gets its own unique QR code that transforms into a beautiful digital menu. Customers scan, browse, and order seamlessly while you focus on what matters most.",
      cta: "See How It Works",
      icon: (
        <FaMobileAlt className="text-4xl sm:text-5xl md:text-6xl text-red-500" />
      ),
      bgGradient: "from-red-500 via-pink-500 to-orange-500",
      features: ["Unique Table Codes", "Beautiful Menus", "Instant Orders"],
    },
    {
      title: "Data-Driven Restaurant Management",
      subtitle:
        "Comprehensive analytics to help you make informed business decisions",
      description:
        "Track sales, monitor popular items, analyze customer behavior, and optimize your menu with our powerful analytics dashboard. Turn data into actionable insights.",
      cta: "View Analytics",
      icon: (
        <FaChartLine className="text-4xl sm:text-5xl md:text-6xl text-amber-500" />
      ),
      bgGradient: "from-amber-500 via-orange-500 to-red-500",
      features: ["Sales Analytics", "Popular Items", "Customer Insights"],
    },
    {
      title: "Seamless Customer Experience",
      subtitle:
        "Delight your customers with a modern, intuitive ordering experience",
      description:
        "From scanning QR codes to receiving orders, every step is designed for maximum convenience. Your customers will love the smooth, contactless experience.",
      cta: "Experience Demo",
      icon: (
        <FaUsers className="text-4xl sm:text-5xl md:text-6xl text-green-500" />
      ),
      bgGradient: "from-green-500 via-emerald-500 to-teal-500",
      features: ["Contactless Experience", "Real-time Updates", "Easy Payment"],
    },
    {
      title: "Custom Digital Solutions",
      subtitle: "Bespoke websites tailored for your hospitality business",
      description:
        "We design and develop fully customized websites that showcase your unique brand identity, menu offerings, and booking capabilities - all optimized for the hospitality industry.",
      cta: "View Portfolio",
      icon: (
        <FaDesktop className="text-4xl sm:text-5xl md:text-6xl text-blue-500" />
      ),
      bgGradient: "from-blue-500 via-indigo-500 to-purple-500",
      features: [
        "Branded Website Design",
        "Menu Integration",
        "Online Reservations",
        "SEO Optimized",
        "Mobile Responsive",
      ],
    },
  ];

  useEffect(() => {
    // Automatic slide disabled; slides only change on arrow click
    // No interval here
  }, []);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  return (
    <section className="relative overflow-hidden py-4 sm:py-6 md:py-10 lg:py-14 xl:py-20 bg-gradient-to-br from-gray-50 to-orange-50">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 relative z-10">
        {/* Slide Container */}
        <div className="relative h-[350px] sm:h-[400px] md:h-[450px] lg:h-[500px] xl:h-[550px] 2xl:h-[600px] overflow-hidden rounded-xl sm:rounded-2xl md:rounded-3xl shadow-2xl bg-gradient-to-br from-black via-gray-900 to-gray-800">
          {/* Particles Background Animation */}
          <Particles
            particleColors={["#ffffff", "#ffffff"]}
            particleCount={200}
            particleSpread={10}
            speed={0.1}
            particleBaseSize={100}
            moveParticlesOnHover={true}
            alphaParticles={false}
            disableRotation={false}
          />
          {/* Slides */}
          {slides.map((slide, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-all duration-700 ease-in-out ${
                index === currentSlide
                  ? "opacity-100 translate-x-0"
                  : "opacity-0 translate-x-full"
              }`}
            >
              <div
                className={`h-full bg-gradient-to-br from-black/80 via-gray-900/80 to-gray-800/80 relative overflow-hidden`}
              >
                {/* Slide Content */}
                <div className="relative z-10 h-full flex items-center">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 md:gap-6 lg:gap-8 xl:gap-12 items-center max-w-6xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
                    {/* Content - Reordered for mobile */}
                    <div className="space-y-2 sm:space-y-3 md:space-y-4 lg:space-y-6 order-2 lg:order-1">
                      <div className="space-y-1.5 sm:space-y-2 md:space-y-3 lg:space-y-4">
                        <h1 className="text-base sm:text-lg md:text-xl lg:text-2xl xl:text-3xl 2xl:text-4xl font-bold leading-tight animate-fadeInUp text-white drop-shadow-lg">
                          {slide.title}
                        </h1>
                        <p className="text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl font-medium text-gray-100 animate-fadeInUp animation-delay-200 drop-shadow">
                          {slide.subtitle}
                        </p>
                        <p className="text-xs sm:text-sm md:text-base lg:text-lg text-gray-200 leading-relaxed animate-fadeInUp animation-delay-400 drop-shadow">
                          {slide.description}
                        </p>
                      </div>
                      {/* Features List */}
                      <div className="flex flex-wrap gap-1 sm:gap-1.5 md:gap-2 lg:gap-3 animate-fadeInUp animation-delay-600">
                        {slide.features.map((feature, idx) => (
                          <span
                            key={idx}
                            className="px-1.5 sm:px-2 md:px-3 lg:px-4 py-0.5 sm:py-1 md:py-1.5 lg:py-2 bg-white/70 backdrop-blur-sm rounded-full text-xs sm:text-sm md:text-base font-medium border border-white/80 text-gray-900 shadow"
                          >
                            {feature}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Visual Element - Rolling Gallery */}
                    <div className="flex justify-center items-center animate-fadeInRight animation-delay-400 order-1 lg:order-2">
                      <div className="relative w-48 sm:w-56 md:w-64 lg:w-72 xl:w-96 2xl:w-[480px] h-40 sm:h-48 md:h-56 lg:h-64 xl:h-80 2xl:h-[400px]">
                        <RollingGallery
                          autoplay={true}
                          pauseOnHover={true}
                          images={
                            [
                              // Slide 1
                              [
                                "https://i.pinimg.com/1200x/81/50/31/8150310a8306e1797bb6b3cabf1a7c44.jpg",
                                "https://i.pinimg.com/1200x/1d/9c/7b/1d9c7bc1a1431abb352a2936a8889548.jpg",
                                "https://i.pinimg.com/1200x/f8/55/55/f85555bb77fcb4872fa7bcd41ba6804f.jpg",
                                "https://i.pinimg.com/736x/76/f0/fc/76f0fc9fb9bc5ac36d389e70d6dd1e77.jpg",
                              ],
                              // Slide 2
                              [
                                "https://i.pinimg.com/736x/25/c8/e3/25c8e341c98f84bcdc8e360165f91955.jpg",
                                "https://i.pinimg.com/1200x/57/c5/4e/57c54ed2ab16a9197c0078f48cdc5a71.jpg",
                                "https://i.pinimg.com/736x/0f/75/4d/0f754d7ed90bbad62e59f1b596d4c1df.jpg",
                                "https://i.pinimg.com/736x/9d/d5/43/9dd543789fcaada9c2978f1f88894219.jpg",
                              ],
                              // Slide 3
                              [
                                "https://i.pinimg.com/736x/6a/00/e3/6a00e3a85e84aac71cb1a14ddd8011ce.jpg",
                                "https://i.pinimg.com/736x/06/f7/d7/06f7d78a7caa8ad91b49ab17f79ad0fb.jpg",
                                "https://i.pinimg.com/1200x/b0/8d/a4/b08da4feb26217c53995eabb36fdad12.jpg",
                                "https://i.pinimg.com/1200x/b0/8d/a4/b08da4feb26217c53995eabb36fdad12.jpg",
                              ],
                              // Slide 4
                              [
                                "https://i.pinimg.com/736x/27/5d/27/275d2768cd140a7aca2ca4397761d0b0.jpg",
                                "https://i.pinimg.com/736x/d2/b7/25/d2b72596ea4ab7c934376cb4ae1a6be2.jpg",
                                "https://i.pinimg.com/736x/bd/88/84/bd8884bf29d29c240c49b8689bdf99cd.jpg",
                                "https://i.pinimg.com/736x/1c/e6/72/1ce672f6cacfcf4592e5c2aa01de7a70.jpg",
                              ],
                              [
                                "https://i.pinimg.com/1200x/74/48/8f/74488f6c98a06a4d37c23dd195e4ff85.jpg",
                                "https://i.pinimg.com/1200x/a8/26/4a/a8264a7a256037b1b70ed3fcd5c51887.jpg",
                                "https://i.pinimg.com/1200x/ec/34/e0/ec34e040c2d9bf41b1ce24b48cdba399.jpg",
                                "https://www.google.com/url?sa=i&url=https%3A%2F%2Fuicookies.com%2Ffree-hotel-website-templates%2F&psig=AOvVaw0jSF46dCaQ5YdurrsvJU0f&ust=1754403499566000&source=images&cd=vfe&opi=89978449&ved=0CBUQjRxqFwoTCMiP3Mas8Y4DFQAAAAAdAAAAABAE",
                              ],
                            ][index]
                          }
                        />
                      </div>
                    </div>
                  </div>

                  {/* CTA Button - Hidden on mobile, visible on tablet and desktop */}
                  <div className="hidden md:block absolute bottom-4 sm:bottom-6 md:bottom-8 lg:bottom-12 right-4 sm:right-6 md:right-8 lg:right-12 animate-fadeInUp animation-delay-800 order-3">
                    <button
                      className="px-3 sm:px-4 md:px-5 lg:px-8 py-2 sm:py-2.5 md:py-3 lg:py-4 bg-white text-gray-900 font-bold rounded-lg sm:rounded-xl hover:bg-gray-200 transition-all duration-300 hover:scale-105 shadow-lg text-xs sm:text-sm md:text-base lg:text-lg"
                      onClick={() => navigate("/login")}
                    >
                      {slide.cta}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}

          {/* Navigation Arrows */}
          <button
            onClick={prevSlide}
            className="absolute left-1 sm:left-2 md:left-3 lg:left-4 top-1/2 transform -translate-y-1/2 w-7 h-7 sm:w-8 sm:h-8 md:w-10 md:h-10 lg:w-12 lg:h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-all duration-300 hover:scale-110 z-20"
            style={{ minWidth: 28, minHeight: 28 }}
            aria-label="Previous slide"
          >
            <FaChevronLeft className="text-xs sm:text-sm md:text-lg lg:text-xl" />
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-1 sm:right-2 md:right-3 lg:right-4 top-1/2 transform -translate-y-1/2 w-7 h-7 sm:w-8 sm:h-8 md:w-10 md:h-10 lg:w-12 lg:h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-all duration-300 hover:scale-110 z-20"
            style={{ minWidth: 28, minHeight: 28 }}
            aria-label="Next slide"
          >
            <FaChevronRight className="text-xs sm:text-sm md:text-lg lg:text-xl" />
          </button>

          {/* Slide Indicators */}
          <div className="absolute bottom-2 sm:bottom-3 md:bottom-4 lg:bottom-6 left-1/2 transform -translate-x-1/2 flex gap-1 sm:gap-1.5 md:gap-2 lg:gap-3 z-20">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`w-1.5 h-1.5 sm:w-2 sm:h-2 md:w-2.5 md:h-2.5 lg:w-3 lg:h-3 rounded-full transition-all duration-300 ${
                  index === currentSlide
                    ? "bg-white scale-125"
                    : "bg-white/50 hover:bg-white/75"
                }`}
                style={{ minWidth: 8, minHeight: 8 }}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>

        {/* Stats Section */}
      </div>
    </section>
  );
}
